import { useEffect, useRef } from "react";
import { readLocal, writeLocal } from "./storage";

/**
 * Single-key shortcuts (UX-01 4.3). They fire only outside text fields, never while an IME is composing,
 * never with Ctrl/Cmd/Alt, and the user can turn them off (WCAG 2.1.4).
 */

const SETTING = "shortcuts";

export function shortcutsEnabled(): boolean {
  return readLocal<string>(SETTING) !== "off";
}

export function setShortcutsEnabled(enabled: boolean): void {
  writeLocal(SETTING, enabled ? "on" : "off");
}

function isTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return !["checkbox", "radio", "button", "submit", "reset"].includes(type);
  }
  return false;
}

/** "y", "Shift+y", "/", "?", "[" … */
export function shortcutName(event: KeyboardEvent): string {
  if (event.key.length === 1 && event.shiftKey && /[a-z]/i.test(event.key)) {
    return `Shift+${event.key.toLowerCase()}`;
  }
  return event.key;
}

export type ShortcutMap = Record<string, (event: KeyboardEvent) => void>;

export function useShortcuts(map: ShortcutMap, active = true): void {
  const ref = useRef(map);
  useEffect(() => {
    ref.current = map;
  });

  useEffect(() => {
    if (!active) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTextTarget(event.target) || !shortcutsEnabled()) return;
      if (document.querySelector("dialog[open]")) return;
      const handler = ref.current[shortcutName(event)];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);
}
